import { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useSKU } from 'hooks/useSKU';
import { useAppSelector } from 'store/hooks';
import { washhubSelector } from 'store/slices/washhub';
import { useMutation, useQueryClient } from 'react-query';
import { successToast } from 'utils/successToast';
import {
  EditRefType,
  EdiWashhubInventoryModalProps,
} from './EditWashhubInventoryModal.props';
import EditWashhubInventoryModalView from './EditWashhubInventoryModal.view';
import { washHub } from 'store/slices';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import { editHubInventoryCount } from 'api/washhub';
import { WashhubInventory } from 'types/washhub';
import { buildEquipmentCountRows } from 'components/modules/EquipmentCountSection';

const Container = forwardRef<EditRefType, EdiWashhubInventoryModalProps>(
  (_, ref) => {
    const queryClient = useQueryClient();
    const washHubId = washHub.selectors.useSelectedWashHubId();
    const washHubs = useAppSelector(washhubSelector);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { mutate, isLoading } = useMutation(editHubInventoryCount);
    const [count, setCount] = useState<WashhubInventory | null>(null);

    const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
      'package',
      washHubId,
    );

    // Fetched with suspended SKUs included, so a count saved against one
    // still shows its name. skipWashHub for the same reason as the Add modal.
    const { data: equipmentSkus = [], isLoading: isLoadingEquipment } = useSKU(
      'equipment',
      undefined,
      undefined,
      undefined,
      true,
    );

    const allItems = skus
      .flatMap((sku) =>
        sku?.items?.map((item) => ({
          sku: item.sku,
          name: item.details.name,
        })),
      )
      .reduce<{ sku: string; name: string }[]>((uniqueItems, currentItem) => {
        if (!uniqueItems.find((item) => item.sku === currentItem?.sku)) {
          currentItem && uniqueItems.push(currentItem);
        }
        return uniqueItems;
      }, []);

    const enrichWithSkuDetails = (entries: any) => {
      return entries.map((entry: any) => {
        const skuDetails = skus.find((sku) => sku._id === entry.sku);
        const items = {};
        if (skuDetails?.type === 'package') {
          Object.assign(items, skuDetails?.items || []);
        }
        return {
          ...entry,
          skuName: skuDetails?.name || '',
          items,
          quantity:
            skuDetails?.items?.reduce((sum, item) => sum + item.quantity, 0) ||
            0,
        };
      });
    };

    const enrichWithItemDetails = (entries: any) => {
      return entries.map((entry: any) => {
        const itemDetails = allItems.find((item) => item?.sku === entry.sku);
        return {
          ...entry,
          skuName: itemDetails?.name || '',
        };
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        onOpen: (_count) => {
          if (_count) {
            // Enrich the count with SKU and Item details
            const enrichedCount = {
              ..._count,
              fullPackages: enrichWithSkuDetails(_count.fullPackages),
              inBoxes: enrichWithItemDetails(_count.inBoxes),
              inProcess: enrichWithItemDetails(_count.inProcess),
              equipment: buildEquipmentCountRows(
                equipmentSkus,
                _count.equipment,
              ),
            };

            setCount(enrichedCount);
          }

          onOpen();
        },
      }),
      [onOpen, allItems, equipmentSkus],
    );

    const onSubmit = (values: WashhubInventory) => {
      values.date = toApiPayloadDateFormat(values.date);
      values._id = count!._id;
      mutate(values, {
        onSuccess: async () => {
          successToast('Hub Inventory Count updated successfully');

          onClose();

          queryClient.invalidateQueries({
            queryKey: ['fetch-hub-inventory-count'],
          });
        },
      });
    };

    const _props = {
      isOpen,
      onClose,
      onSubmit,
      washHubs,
      isLoading: isLoadingSKU || isLoadingEquipment || isLoading,
      count,
    };
    return <EditWashhubInventoryModalView {..._props} />;
  },
);
const EditWashhubInventoryModal = memo(Container);

export default EditWashhubInventoryModal;
